import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { LanguageSelector } from './LanguageSelector';
import { Menu, X, Zap, Calculator, BookOpen, Mail } from 'lucide-react';
import { getProviderDataBySlug } from '../config/providersData';

export const Header = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const isSpanish = i18n.language === 'es';
  const langPrefix = isSpanish ? '/es' : '/fr';

  const navigationItems = [
    { key: 'home', path: langPrefix, icon: Zap },
    { 
      key: 'tools', 
      path: `${langPrefix}/${isSpanish ? 'herramientas' : 'outils'}`,
      icon: Calculator,
      dropdown: [
        { key: 'ebooks', path: `${langPrefix}/ebooks`, icon: BookOpen },
        { key: 'templates', path: `${langPrefix}/${isSpanish ? 'modelos-email' : 'modeles-emails'}`, icon: Mail },
        { key: 'moving', path: `${langPrefix}/${isSpanish ? 'mudanza' : 'demenagement'}` },
        { key: 'tracker', path: `${langPrefix}/${isSpanish ? 'seguimiento-precios' : 'tracker-prix'}` },
        { key: 'solar', path: `${langPrefix}/${isSpanish ? 'simulador-solar' : 'simulateur-solaire'}` },
        { key: 'expenses', path: `${langPrefix}/${isSpanish ? 'seguimiento-gastos' : 'suivi-depenses'}` },
        { key: 'inflation', path: `${langPrefix}/${isSpanish ? 'inflacion' : 'inflation'}` },
        { key: 'internet', path: `${langPrefix}/${isSpanish ? 'comparador-internet' : 'comparateur-internet'}` },
        { key: 'electricity', path: `${langPrefix}/${isSpanish ? 'luz' : 'comparateur-electricite'}` }
      ]
    },
    !isSpanish && { 
      key: 'guide', 
      path: `${langPrefix}/guide-anti-inflation`,
      icon: BookOpen,
      dropdown: [
        { key: 'guide-home', path: `${langPrefix}/guide-anti-inflation`, label: 'Guide Complet', icon: BookOpen },
        { key: 'chapter1', path: `${langPrefix}/guide-anti-inflation`, hash: 'chapitre-1', label: 'Ch.1 - Énergie' },
        { key: 'chapter2', path: `${langPrefix}/guide-anti-inflation`, hash: 'chapitre-2', label: 'Ch.2 - Internet' },
        { key: 'chapter3', path: `${langPrefix}/guide-anti-inflation`, hash: 'chapitre-3', label: 'Ch.3 - Assurances' },
        { key: 'chapter4', path: `${langPrefix}/guide-anti-inflation`, hash: 'chapitre-4', label: 'Ch.4 - Courses' },
        { key: 'chapter5', path: `${langPrefix}/guide-anti-inflation`, hash: 'chapitre-5', label: 'Ch.5 - Banque' },
        { key: 'chapter6', path: `${langPrefix}/guide-anti-inflation`, hash: 'chapitre-6', label: 'Ch.6 - Transport' },
        { key: 'chapter7', path: `${langPrefix}/guide-anti-inflation`, hash: 'chapitre-7', label: 'Ch.7 - Voyages' },
        { key: 'chapter8', path: `${langPrefix}/guide-anti-inflation`, hash: 'chapitre-8', label: 'Ch.8 - Shopping' },
        { key: 'chapter9', path: `${langPrefix}/guide-anti-inflation`, hash: 'chapitre-9', label: 'Ch.9 - Streaming' },
        { key: 'products', path: `${langPrefix}/produits-testes`, label: '🛒 Produits Testés' }
      ]
    }
  ].filter(Boolean);

  const isActivePath = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Détecter si on est sur une page fournisseur
  const isProviderPage = location.pathname.includes('/fournisseur/') || location.pathname.includes('/fournisseurs/') || location.pathname.includes('/proveedor/') || location.pathname.includes('/proveedores/');

  // Extraire les infos du fournisseur courant pour la popup de rétention
  const getProviderContext = () => {
    if (!isProviderPage) return null;
    const urlParts = location.pathname.split('/').filter(p => p !== '');
    const slug = urlParts[urlParts.length - 1];
    const providerData = getProviderDataBySlug(slug);
    if (providerData) {
      return { fromProvider: true, country: providerData.country, vertical: providerData.vertical };
    }
    // Fallback basé sur l'URL
    const country = location.pathname.startsWith('/es') ? 'ES' : 'FR';
    const vertical = location.pathname.includes('internet') ? 'internet' : 'electricity';
    return { fromProvider: true, country, vertical };
  };

  const logoTarget = isSpanish ? '/es' : '/fr';
  const providerContext = getProviderContext();

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-sage-100 transition-all duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-11">
          {/* Logo - Toujours visible */}
          <Link 
            to={logoTarget}
            state={providerContext}
            className="flex items-center gap-2 font-bold text-base text-sage-800 hover:text-sage-600 transition-colors"
            data-testid="header-logo"
          >
            <div className="w-6 h-6 bg-gradient-to-br from-sage-600 to-sage-700 rounded-md flex items-center justify-center">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold">SAVEA</span>
          </Link>

          {/* Desktop Navigation - Caché sur mobile pages fournisseurs */}
          <nav className={`hidden lg:flex items-center gap-8`}>
            {navigationItems.map((item) => (
              <div key={item.key} className="relative group">
                <Link
                  to={item.path}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    isActivePath(item.path)
                      ? 'bg-sage-100 text-sage-800'
                      : 'text-sage-600 hover:text-sage-800 hover:bg-sage-50'
                  }`}
                  data-testid={`nav-${item.key}`}
                >
                  <item.icon className="w-4 h-4" />
                  {t(`nav.${item.key}`)}
                </Link>
                
                {/* Dropdown Menu */}
                {item.dropdown && (
                  <div className="absolute top-full left-0 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                    <div className="bg-white/95 backdrop-blur-sm rounded-lg shadow-lg border border-sage-100 py-2 min-w-[220px]">
                      {item.dropdown.map((dropdownItem) => {
                        // Si c'est un lien avec hash, utiliser onClick pour scroll
                        if (dropdownItem.hash) {
                          return (
                            <button
                              key={dropdownItem.key}
                              onClick={() => {
                                window.location.href = `${dropdownItem.path}#${dropdownItem.hash}`;
                                setTimeout(() => {
                                  const element = document.getElementById(dropdownItem.hash);
                                  if (element) {
                                    const offset = 80;
                                    const elementPosition = element.getBoundingClientRect().top;
                                    const offsetPosition = elementPosition + window.pageYOffset - offset;
                                    window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                                  }
                                }, 100);
                              }}
                              className="w-full text-left flex items-center gap-3 px-4 py-2 text-sm text-sage-700 hover:bg-sage-50 hover:text-sage-800 transition-colors"
                            >
                              {dropdownItem.icon && <dropdownItem.icon className="w-4 h-4" />}
                              {dropdownItem.label || t(`nav.${dropdownItem.key}`)}
                            </button>
                          );
                        }
                        // Sinon, lien normal
                        return (
                          <Link
                            key={dropdownItem.key}
                            to={dropdownItem.path}
                            className="flex items-center gap-3 px-4 py-2 text-sm text-sage-700 hover:bg-sage-50 hover:text-sage-800 transition-colors"
                            data-testid={`dropdown-${dropdownItem.key}`}
                          >
                            {dropdownItem.icon && <dropdownItem.icon className="w-4 h-4" />}
                            {dropdownItem.label || t(`nav.${dropdownItem.key}`)}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Right Section - Masqué sur mobile pour pages fournisseurs */}
          <div className="flex items-center gap-4">
            {/* Language selector - Desktop ou non-provider pages */}
            <div className={isProviderPage ? 'hidden lg:block' : ''}>
              <LanguageSelector />
            </div>
            
            {/* CTA Button - Desktop uniquement ou non-provider pages */}
            <Button
              asChild
              className={`${isProviderPage ? 'hidden lg:flex' : 'hidden sm:flex'} bg-gradient-to-r from-sage-600 to-sage-700 hover:from-sage-700 hover:to-sage-800 text-white px-6 py-2 rounded-full transition-all duration-200 shadow-lg hover:shadow-xl`}
              data-testid="header-cta-button"
            >
              <Link to={`${langPrefix}/${isSpanish ? 'herramientas' : 'outils'}`}>{t('hero.cta')}</Link>
            </Button>

            {/* Mobile Menu Toggle - Masqué sur pages fournisseurs mobile */}
            {!isProviderPage && (
              <Button
                variant="ghost"
                size="sm"
                className="lg:hidden"
                onClick={toggleMobileMenu}
                data-testid="mobile-menu-toggle"
              >
                {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            )}
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-sage-100 bg-white/95 backdrop-blur-sm">
            <nav className="space-y-2">
              {navigationItems.map((item) => (
                <div key={item.key}>
                  <Link
                    to={item.path}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                      isActivePath(item.path)
                        ? 'bg-sage-100 text-sage-800'
                        : 'text-sage-600 hover:text-sage-800 hover:bg-sage-50'
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                    data-testid={`mobile-nav-${item.key}`}
                  >
                    <item.icon className="w-4 h-4" />
                    {t(`nav.${item.key}`)}
                  </Link>
                  
                  {/* Mobile Dropdown */}
                  {item.dropdown && (
                    <div className="pl-8 mt-2 space-y-1">
                      {item.dropdown.map((dropdownItem) => (
                        <Link
                          key={dropdownItem.key}
                          to={dropdownItem.path}
                          className="flex items-center gap-3 px-4 py-2 text-sm text-sage-600 hover:bg-sage-50 hover:text-sage-700 rounded-md transition-colors"
                          onClick={() => setIsMobileMenuOpen(false)}
                          data-testid={`mobile-dropdown-${dropdownItem.key}`}
                        >
                          {dropdownItem.icon && <dropdownItem.icon className="w-4 h-4" />}
                          {t(`nav.${dropdownItem.key}`)}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              
              <Button
                asChild
                className="w-full mt-4 bg-gradient-to-r from-sage-600 to-sage-700 hover:from-sage-700 hover:to-sage-800 text-white"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <Link to={`${langPrefix}/${isSpanish ? 'herramientas' : 'outils'}`}>{t('hero.cta')}</Link>
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};